import React, { forwardRef, useMemo } from "react";
import { Platform, TouchableWithoutFeedback, View } from "react-native";

import { ExclamationCircleSolid } from "@smartrent/icons";

import { useTheme } from "../../utils/theme";
import { CheckboxBase } from "../CheckboxBase";
import { AssistiveText } from "../AssistiveText";
import { CheckboxLabel } from "../CheckboxLabel";
import { Typography } from "../Typography";

import type { CheckboxBaseProps, CheckboxRef } from "../CheckboxBase";
import type { TestProps } from "../../types";
import type { WebAccessibilityRole } from "react-native";
import type { ReactNode } from "react";

export interface CheckboxProps
  extends Omit<CheckboxBaseProps, "ariaInvalid" | "ariaRequired" | "labelID">,
    TestProps {
  label: string | ReactNode;
  assistiveText?: string | ReactNode;
  required?: boolean;
  requiredText?: string;
  error?: string;
}

export const Checkbox = forwardRef(function Checkbox(
  {
    name,
    assistiveText,
    required,
    requiredText = "Required",
    label,
    disabled,
    error,
    testID,
    style,
    ...props
  }: CheckboxProps,
  ref: CheckboxRef
) {
  const { colors, spacing } = useTheme();
  const labelID = useMemo(() => `${name}Label`, [name]);
  const {
    helperText,
    helperTextColor,
    disabledProp,
    ariaRequired,
    ariaInvalid,
  } = useMemo(() => {
    let ariaRequired = false;
    let ariaInvalid = false;
    let helperText: string | undefined;
    let helperTextColor: string | undefined;
    let disabledProp: boolean | undefined;

    if (disabled) {
      disabledProp = true;
    } else {
      if (required) {
        ariaRequired = required;
        helperText = requiredText;
      }

      if (error) {
        ariaInvalid = error ? true : false;
        helperText = error;
        helperTextColor = colors.errorPrimary;
      }
    }

    return {
      ariaRequired,
      ariaInvalid,
      helperText,
      helperTextColor,
      disabledProp,
    };
  }, [disabled, required, error, requiredText, colors.errorPrimary]);

  return (
    <View style={style} testID={testID}>
      {assistiveText ? <AssistiveText>{assistiveText}</AssistiveText> : null}
      <TouchableWithoutFeedback
        accessible={false}
        onPress={() => !disabledProp && props.onChangeChecked(!props.checked)}
      >
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            paddingVertical: spacing.narrow,
          }}
        >
          <CheckboxBase
            ref={ref}
            name={name}
            ariaInvalid={ariaInvalid}
            ariaRequired={ariaRequired}
            disabled={disabledProp}
            labelID={labelID}
            {...props}
          />
          <View
            nativeID={labelID}
            style={{
              marginLeft: spacing.sm,
              marginRight: "auto",
              flex: 1,
            }}
            accessibilityRole={Platform.select<WebAccessibilityRole>({
              web: "label",
              default: "text", // `label` is not a valid accessibilityRole in react-native but is necessary for react-native-web
            })}
          >
            {typeof label === "string" ? (
              <CheckboxLabel>{label}</CheckboxLabel>
            ) : (
              label
            )}
          </View>
          {ariaInvalid ? (
            <ExclamationCircleSolid
              size={20}
              color={colors.errorPrimary}
              style={{
                marginLeft: spacing.sm,
                flexShrink: 0,
              }}
            />
          ) : null}
        </View>
      </TouchableWithoutFeedback>
      {helperText ? (
        <Typography
          accessible={ariaInvalid}
          aria-describedby={name}
          type="caption"
          style={{
            color: helperTextColor ? helperTextColor : colors.textCaption,
            marginLeft: spacing.xxl,
          }}
        >
          {helperText}
        </Typography>
      ) : null}
    </View>
  );
});
Checkbox.displayName = "Checkbox";
